import Effects from 'Effects';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AudioParamPercentageValueParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';

class CompressorEffect extends Effects.Effect {

    constructor(sequencerEngine) {
        super('Compressor', () => new Tone.Gain(1), sequencerEngine); 
        this.effectNode.gain.value = 1;
        this.compressorNode = new Tone.Compressor(-24, 3);
        this.effectNode.connect(this.compressorNode);

        this.gainParameter = new AudioParamValueParameter(
            'Gain', this.effectNode.gain, {
                documentation: "Controls the gain (or loudness) of the signal before it hits the compressor",
                min: 0,
                max: 1,
                unit: 'db',
                defaultValue: 1
            });
        this.registerParameter(this.gainParameter);
        this.thresholdParameter = new AudioParamValueParameter(
            'Threshold', this.compressorNode.threshold, {
                documentation: "The threshold of volume at which the compressor begins to compress.",
                min: -80,
                max: 0,
                unit: '',
                defaultValue: -4,
                constantAutomationRate: true,
                automatable: false,
            });
        this.registerParameter(this.thresholdParameter);
        this.ratioParameter = new AudioParamValueParameter(
            'Ratio', this.compressorNode.ratio, {
                documentation: "The amount of compression that occurs once the signal passes the threshold.",
                min: 1,
                max: 24,
                unit: '',
                defaultValue: 3, 
                constantAutomationRate: true,
            });
        this.registerParameter(this.ratioParameter);
        this.attackParameter = new AudioParamPercentageValueParameter(
            'Attack', this.compressorNode.attack,
            {
                constantAutomationRate: true,
            });
        this.registerParameter(this.attackParameter);
        this.releaseParameter = new AudioParamPercentageValueParameter(
            'Release', this.compressorNode.release,
            {
                constantAutomationRate: true
            });
        this.registerParameter(this.releaseParameter);
        this.releaseParameter.set(0.15);
        this.kneeParameter = new AudioParamValueParameter(
            'Knee', this.compressorNode.knee, {
                min: 0,
                max: 40,
                unit: 'db',
                defaultValue: 1,
                constantAutomationRate: true
            });
        this.registerParameter(this.kneeParameter);
        this.updateInitialValues();
    }

    get documented() {
        return "Reduces the volume of loud sounds or amplifies quiet sounds.";
    }

    dispose() {
        super.dispose();
        this.compressorNode.dispose();
        this.compressorNode = undefined;
    }

    connect(node) {
        this.connectedNode = node;
        this.compressorNode.connect(node);
    }

    disconnect(node) {
        if (this.connectedNode != null) {
            this.compressorNode.disconnect(node);
            this.connectedNode = null;
        }
    }

    getJson(isPreset) {
        let json = super.getJson();
        json.name = this.name;
        json.gain = this.gainParameter.get();
        json.threshold = this.thresholdParameter.get();
        json.ratio = this.ratioParameter.get();
        json.release = this.releaseParameter.get();
        json.attack = this.attackParameter.get();
        json.knee = this.kneeParameter.get();
        json.enabled = this.enabledParameter.get();

        json = this.saveParameterToJson(json, 'gainParameter', this.gainParameter);

        if (this.thresholdParameter.isConnectedToAnyModulator() ||
            Object.keys(this.thresholdParameter.patternToValue).length > 0) {
            json.thresholdParameter = this.thresholdParameter.getJson();
        }
        if (this.ratioParameter.isConnectedToAnyModulator() ||
            Object.keys(this.ratioParameter.patternToValue).length > 0) {
            json.ratioParameter = this.ratioParameter.getJson();
        }
        if (this.releaseParameter.isConnectedToAnyModulator() ||
            Object.keys(this.releaseParameter.patternToValue).length > 0) {
            json.releaseParameter = this.releaseParameter.getJson();
        }
        if (this.attackParameter.isConnectedToAnyModulator() ||
            Object.keys(this.attackParameter.patternToValue).length > 0) {
            json.attackParameter = this.attackParameter.getJson();
        }
        if (this.kneeParameter.isConnectedToAnyModulator() |
            Object.keys(this.kneeParameter.patternToValue).length > 0) {
            json.kneeParameter = this.kneeParameter.getJson();
        }
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        if ('gain' in json) {
            bulkUpdate(this.gainParameter, json.gain, bulk);
        }
        if ('threshold' in json) {
            bulkUpdate(this.thresholdParameter, json.threshold, bulk);
        }
        if ('ratio' in json) {
            bulkUpdate(this.ratioParameter, json.ratio, bulk);
        }
        if ('release' in json) {
            bulkUpdate(this.releaseParameter, json.release, bulk);
        }
        if ('attack' in json) {
            bulkUpdate(this.attackParameter, json.attack, bulk);
        }
        if ('knee' in json) {
            bulkUpdate(this.kneeParameter, json.knee, bulk);
        }
        if (json.enabled) {
            this.enable(true);
        }

        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'gainParameter', this.gainParameter, isCopy),
            ... this.loadParameterFromJson(json, 'thresholdParameter', this.thresholdParameter, isCopy),
            ... this.loadParameterFromJson(json, 'ratioParameter', this.ratioParameter, isCopy),
            ... this.loadParameterFromJson(json, 'releaseParameter', this.releaseParameter, isCopy),
            ... this.loadParameterFromJson(json, 'attackParameter', this.attackParameter, isCopy),
            ... this.loadParameterFromJson(json, 'kneeParameter', this.kneeParameter, isCopy)
        };
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new CompressorEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

export default CompressorEffect;
